import { useState, useEffect } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const METHODS = ['Check', 'ACH / Bank Transfer', 'Venmo', 'PayPal'];

const FIELD_LABELS = {
  'Check': ['Payee / business name', 'Mailing address'],
  'ACH / Bank Transfer': ['Account holder / business name', 'Bank/account information', 'Account type (checking/savings)'],
  'Venmo': ['Venmo username or approved payment identifier'],
  'PayPal': ['PayPal email / payment identifier'],
};

export default function FarmPayoutInfo() {
  const [payout, setPayout] = useState({ method: '', account_details: '', confirmed: false });
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/farm/payout-info').then((r) => {
      if (r.data?.method) { setPayout({ method: r.data.method, account_details: r.data.account_details, confirmed: r.data.confirmed }); setSavedAt(r.data.updated_at); }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setPayout((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!payout.method) { toast.error('Please select a payout method'); return; }
    if (!payout.account_details) { toast.error('Please enter your account/payment details'); return; }
    if (!payout.confirmed) { toast.error('Please confirm the information is accurate'); return; }
    setLoading(true);
    try {
      const r = await api.put('/farm/payout-info', payout);
      toast.success(r.data.message);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save payout info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="farm-payout-info">
      <div className="flex items-center gap-2 mb-2"><Lock className="w-4 h-4 text-forest" /><h1 className="font-playfair text-2xl font-bold text-dark">Payout Info</h1></div>
      <p className="font-manrope text-dark/60 mb-6">Private and visible only to you and authorized FarmBox Admin. Never shown on your public profile or to other farm partners.</p>

      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 space-y-5">
        <div>
          <Label className="mb-2 block">Payout method</Label>
          <div className="flex flex-wrap gap-2">
            {METHODS.map((m) => (
              <label key={m} className={`px-4 py-2.5 rounded-full border cursor-pointer text-sm font-manrope ${payout.method === m ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`} data-testid={`payout-method-${m.split(' ')[0].toLowerCase()}`}>
                <input type="radio" className="hidden" checked={payout.method === m} onChange={() => set('method', m)} />{m}
              </label>
            ))}
          </div>
        </div>

        {payout.method && (
          <div>
            <Label>Account / Payment Details</Label>
            <p className="font-manrope text-xs text-dark/50 mb-2">For {payout.method}, include: {FIELD_LABELS[payout.method].join(', ')}</p>
            <textarea
              className="w-full min-h-[110px] rounded-xl bg-cream border border-forest/20 p-3 font-manrope text-sm"
              value={payout.account_details}
              onChange={(e) => set('account_details', e.target.value)}
              placeholder={FIELD_LABELS[payout.method].join('\n')}
              data-testid="payout-account-details"
            />
          </div>
        )}

        <label className="flex items-start gap-3 cursor-pointer" data-testid="payout-confirm-checkbox">
          <Checkbox checked={payout.confirmed} onCheckedChange={(v) => set('confirmed', v)} className="mt-1" />
          <span className="font-manrope text-sm text-dark">I confirm that the payout information provided is accurate and that I am authorized to receive payments on behalf of this business.</span>
        </label>

        <button onClick={save} disabled={loading} className="btn-primary w-full justify-center" data-testid="save-payout-info">
          {loading ? 'Saving…' : 'Save Payout Info'}
        </button>

        {savedAt && (
          <p className="font-manrope text-xs text-forest flex items-center gap-1.5" data-testid="payout-saved-indicator"><ShieldCheck className="w-3.5 h-3.5" />Payout info on file</p>
        )}
      </div>
    </div>
  );
}
